import { useEffect, useRef, useState } from "react";
import { collection, doc, onSnapshot, query, updateDoc, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "../firebaseConfig";
import { useAuth } from "../AuthContext";
import Header from "../components/HeaderV2";
import { BlockInput, SelectorInput } from "../components/Inputs";
import ErrMsg from "../ErrMsg";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/AdminV2.module.css";

interface AdminType {
    timestamp: number;
    email: string;
    name: string;
    partnerId?: string;
    type: string;
    uid: string;
}

const EMAIL_PATTERN = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;
type EmailCheckStatus = "idle" | "checking" | "available" | "unavailable" | "stale";

const AdminV2 = () => {
    const { userType } = useAuth();

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>계정 관리</h4>
                    <p className={common["text--copy"]}>
                        내 계정 정보를 관리하고 같은 파트너사 관리자 계정을 추가하거나 수정합니다.
                    </p>

                    <MyAccount />

                    {userType === "GROUP_ADMIN" && <AdminTable />}
                </div>
            </div>
        </>
    );
};

const MyAccount = () => {
    const { user, userType } = useAuth();
    const [admin, setAdmin] = useState<AdminType | null>(null);
    const [changeName, setChangeName] = useState<boolean>(false);
    const [changePassword, setChangePassword] = useState<boolean>(false);

    useEffect(() => {
        if (!user?.uid) return;

        const unsubscribe = onSnapshot(doc(db, "admin", user.uid), (snapshot) => {
            setAdmin(snapshot.data() as AdminType);
        });
        return () => unsubscribe();
    }, [user?.uid]);

    return (
        <section className={style["account-card"]}>
            <div className={style["account-main"]}>
                <div className={style["avatar"]}>{(admin?.name || user?.email || "P").slice(0, 1).toUpperCase()}</div>
                <div className={style["account-copy"]}>
                    <p className={style["section-kicker"]}>내 계정</p>
                    <h5>{admin?.name || "이름 없음"}</h5>
                    <p>{user?.email || admin?.email || "-"}</p>
                </div>
            </div>
            <div className={style["account-meta"]}>
                <InfoItem label="권한" value={typeLabel(admin?.type || userType)} />
                <InfoItem label="파트너사" value={admin?.partnerId || sessionStorage.getItem("partnerId") || "-"} />
            </div>
            <div className={style["account-actions"]}>
                <button className={style["button--secondary"]} onClick={() => setChangeName(true)}>
                    이름 변경
                </button>
                <button className={style["button--primary"]} onClick={() => setChangePassword(true)}>
                    비밀번호 변경
                </button>
            </div>

            {changeName && <ChangeName currentName={admin?.name || ""} close={() => setChangeName(false)} />}
            {changePassword && <ChangePassword close={() => setChangePassword(false)} />}
        </section>
    );
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
    <div className={style["info-item"]}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const AdminTable = () => {
    const { user } = useAuth();
    const partnerId = sessionStorage.getItem("partnerId") ?? "";
    const [admins, setAdmins] = useState<AdminType[]>([]);
    const [search, setSearch] = useState<string>("");
    const [roleFilter, setRoleFilter] = useState<string>("ALL");
    const [createAdmin, setCreateAdmin] = useState<boolean>(false);
    const [editAdmin, setEditAdmin] = useState<boolean>(false);
    const [target, setTarget] = useState<AdminType | null>(null);

    useEffect(() => {
        if (!partnerId) return;

        const unsubscribe = onSnapshot(
            query(collection(db, "admin"), where("partnerId", "==", partnerId)),
            (snapshot) => {
                const adminLists = snapshot.docs
                    .map((doc) => doc.data() as AdminType)
                    .sort((a, b) => b.timestamp - a.timestamp);
                setAdmins(adminLists);
            }
        );
        return () => unsubscribe();
    }, [partnerId]);

    useEffect(() => {
        document.body.style.overflow = createAdmin || editAdmin ? "hidden" : "auto";
    }, [createAdmin, editAdmin]);

    const filteredAdmins = admins.filter((admin) => {
        const keyword = search.toLowerCase();
        const matchesKeyword =
            search === "" ||
            admin.name.toLowerCase().includes(keyword) ||
            admin.email.toLowerCase().includes(keyword);
        const matchesRole = roleFilter === "ALL" || admin.type === roleFilter;
        return matchesKeyword && matchesRole;
    });

    return (
        <>
            <section className={style["admin-panel"]}>
                <div className={style["panel-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>관리자 목록</p>
                        <h5 className={style["panel-title"]}>파트너 관리자</h5>
                        <p className={style["panel-copy"]}>
                            총 {admins.length}개 계정 중 {filteredAdmins.length}개 표시
                        </p>
                    </div>
                    <button className={style["button--primary"]} onClick={() => setCreateAdmin(true)}>
                        관리자 추가
                    </button>
                </div>

                <div className={style["toolbar"]}>
                    <input
                        className={common["input--kiosk-filter"]}
                        type="text"
                        placeholder="이름 또는 이메일로 검색"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className={style["segment"]}>
                        <button className={roleFilter === "ALL" ? style["segment--active"] : ""} onClick={() => setRoleFilter("ALL")}>
                            전체
                        </button>
                        <button
                            className={roleFilter === "GROUP_ADMIN" ? style["segment--active"] : ""}
                            onClick={() => setRoleFilter("GROUP_ADMIN")}
                        >
                            최고
                        </button>
                        <button
                            className={roleFilter === "ADMIN" ? style["segment--active"] : ""}
                            onClick={() => setRoleFilter("ADMIN")}
                        >
                            일반
                        </button>
                    </div>
                </div>

                <div className={style["wrapper--admin-list"]}>
                    <div className={[style["container--rows"], style["container--rows-title"]].join(" ")}>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>이메일</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>이름</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>권한</p>
                        <p className={[style["text--eyebrow"], style["text--eyebrow--title"]].join(" ")}>관리</p>
                    </div>
                    {filteredAdmins.length === 0 && <div className={style["empty-state"]}>검색 결과가 없습니다.</div>}
                    {filteredAdmins.map((admin) => (
                        <div
                            key={admin.uid}
                            className={[style["unit--admin-list"], admin.uid === user.uid && style["unit--admin-list--me"]]
                                .filter(Boolean)
                                .join(" ")}
                        >
                            <div className={style["container--rows"]}>
                                <p className={style["text--eyebrow"]}>{admin.email}</p>
                                <p className={style["text--eyebrow"]}>{admin.name}</p>
                                <p className={style["text--eyebrow"]}>
                                    <span className={style["role-badge"]}>{typeLabel(admin.type)}</span>
                                </p>
                                <div className={style["row-actions"]}>
                                    {admin.uid === user.uid ? (
                                        <span className={style["me-badge"]}>나</span>
                                    ) : (
                                        <button
                                            className={style["button--ghost"]}
                                            onClick={() => {
                                                setTarget(admin);
                                                setEditAdmin(true);
                                            }}
                                        >
                                            관리
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {createAdmin && <CreateAdmin close={() => setCreateAdmin(false)} />}
            {editAdmin && target && <EditAdmin admin={target} close={() => setEditAdmin(false)} />}
        </>
    );
};

const ChangeName = ({ currentName, close }: { currentName: string; close: () => void }) => {
    const { user } = useAuth();
    const [name, setName] = useState<string>(currentName);
    const [nameError, setNameError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);

    useEffect(() => setActive(true), []);
    useEffect(() => setNameError(name && name.length < 2 ? ErrMsg["auth/name-invalid"] : ""), [name]);

    const hide = () => {
        setActive(false);
        setTimeout(close, 240);
    };

    const change = async () => {
        if (!name) {
            setNameError(ErrMsg["auth/name-empty"]);
            return;
        }

        setLoading(true);
        try {
            await updateDoc(doc(db, "admin", user.uid), { name });
            hide();
        } catch (err) {
            console.error(err);
            alert("관리자 정보 수정 중에 에러가 발생했습니다");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal active={active} title="이름 변경" kicker="내 계정" copy="관리자 목록과 사이드바에 표시되는 이름입니다." close={hide}>
            <div className={common["wrapper--overlay"]}>
                <div className={style["unit--overlay--inputs"]}>
                    <BlockInput type="text" placeholder="이름" value={name} setValue={setName} error={nameError} />
                </div>
            </div>
            <ModalFooter close={hide} primaryText={loading ? "저장 중" : "저장"} onPrimary={change} />
        </Modal>
    );
};

const ChangePassword = ({ close }: { close: () => void }) => {
    const { passwordChange } = useAuth();
    const [password, setPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [newPasswordError, setNewPasswordError] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);

    useEffect(() => setActive(true), []);
    useEffect(() => setPasswordError(password && password.length < 6 ? ErrMsg["auth/password-invalid"] : ""), [password]);
    useEffect(() => setNewPasswordError(newPassword && newPassword.length < 6 ? ErrMsg["auth/password-invalid"] : ""), [newPassword]);
    useEffect(() => {
        setConfirmPasswordError(confirmPassword && newPassword !== confirmPassword ? ErrMsg["auth/password-not-match"] : "");
    }, [confirmPassword, newPassword]);

    const hide = () => {
        setActive(false);
        setTimeout(close, 240);
    };

    const change = async () => {
        if (!password) return setPasswordError(ErrMsg["auth/password-empty"]);
        if (!newPassword) return setNewPasswordError(ErrMsg["auth/password-empty"]);
        if (!confirmPassword) return setConfirmPasswordError(ErrMsg["auth/password-empty"]);

        setLoading(true);
        try {
            await passwordChange(password, newPassword);
            hide();
        } catch (err: any) {
            console.error(err);
            if (err.message === "auth/wrong-password") setPasswordError(ErrMsg[err.message]);
            else alert("비밀번호 변경 중 에러가 발생했습니다");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal active={active} title="비밀번호 변경" kicker="보안 설정" copy="현재 비밀번호 확인 후 새 비밀번호로 변경합니다." close={hide}>
            <div className={common["wrapper--overlay"]}>
                <div className={style["unit--overlay--inputs"]}>
                    <BlockInput type="password" placeholder="현재 비밀번호" value={password} setValue={setPassword} error={passwordError} />
                    <BlockInput type="password" placeholder="새로운 비밀번호" value={newPassword} setValue={setNewPassword} error={newPasswordError} />
                    <BlockInput
                        type="password"
                        placeholder="새로운 비밀번호 확인"
                        value={confirmPassword}
                        setValue={setConfirmPassword}
                        error={confirmPasswordError}
                    />
                </div>
            </div>
            <ModalFooter close={hide} primaryText={loading ? "저장 중" : "변경"} onPrimary={change} />
        </Modal>
    );
};

const CreateAdmin = ({ close }: { close: () => void }) => {
    const init = useRef(true);
    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string>("");
    const [type, setType] = useState<string>("");
    const [typeError, setTypeError] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");
    const [emailCheckStatus, setEmailCheckStatus] = useState<EmailCheckStatus>("idle");
    const [emailCheckMessage, setEmailCheckMessage] = useState<string>("");
    const [checkedEmail, setCheckedEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [confirm, setConfirm] = useState<string>("");
    const [confirmError, setConfirmError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);

    useEffect(() => setActive(true), []);
    useEffect(() => setNameError(name && name.length < 2 ? ErrMsg["auth/name-invalid"] : ""), [name]);
    useEffect(() => {
        if (init.current) {
            init.current = false;
            return;
        }
        setTypeError(!type ? ErrMsg["auth/type-invalid"] : "");
    }, [type]);
    useEffect(() => {
        if (!email) {
            setEmailError("");
            setEmailCheckStatus("idle");
            setEmailCheckMessage("");
            setCheckedEmail("");
            return;
        }

        if (!EMAIL_PATTERN.test(email)) {
            setEmailError(ErrMsg["auth/email-invalid"]);
            setEmailCheckStatus("idle");
            setEmailCheckMessage("");
            setCheckedEmail("");
            return;
        }

        setEmailError("");

        if (checkedEmail && email !== checkedEmail) {
            setEmailCheckStatus("stale");
            setEmailCheckMessage("이메일이 변경되었습니다. 중복체크를 다시 진행해주세요.");
        }
    }, [email, checkedEmail]);
    useEffect(() => setPasswordError(password && password.length < 6 ? ErrMsg["auth/password-invalid"] : ""), [password]);
    useEffect(() => setConfirmError(confirm && confirm !== password ? ErrMsg["auth/password-not-match"] : ""), [confirm, password]);

    const hide = () => {
        setActive(false);
        setTimeout(close, 240);
    };

    const checkEmail = () => {
        const nextEmail = email.trim();
        if (!nextEmail) {
            setEmailError(ErrMsg["auth/email-empty"]);
            return;
        }
        if (!EMAIL_PATTERN.test(nextEmail)) {
            setEmailError(ErrMsg["auth/email-invalid"]);
            return;
        }

        if (nextEmail !== email) {
            setEmail(nextEmail);
        }

        setEmailError("");
        setEmailCheckStatus("checking");
        setEmailCheckMessage("이메일 중복 확인 중입니다.");

        return httpsCallable(functions, "adminEmailCheck")({ email: nextEmail })
            .then((result) => {
                const data = result.data as {
                    code?: boolean;
                    available?: boolean;
                    message?: string;
                };

                setCheckedEmail(nextEmail);

                if (data.available) {
                    setEmailCheckStatus("available");
                    setEmailCheckMessage("사용 가능한 이메일입니다.");
                } else {
                    setEmailCheckStatus("unavailable");
                    setEmailCheckMessage(ErrMsg[data.message || "auth/email-already-in-use"] || "이미 사용 중인 이메일입니다.");
                }
            })
            .catch((err) => {
                console.error(err.message);
                setCheckedEmail("");
                setEmailCheckStatus("unavailable");
                setEmailCheckMessage("중복 확인에 실패했습니다. 다시 시도해주세요.");
            });
    };

    const create = async () => {
        if (nameError || typeError || emailError || passwordError || confirmError) return;
        const nextEmail = email.trim();
        if (!name) return setNameError(ErrMsg["auth/name-empty"]);
        if (!type) return setTypeError(ErrMsg["auth/type-invalid"]);
        if (!nextEmail) return setEmailError(ErrMsg["auth/email-empty"]);
        if (emailCheckStatus === "checking") {
            setEmailCheckMessage("이메일 중복 확인이 끝난 뒤 추가해주세요.");
            return;
        }
        if (emailCheckStatus !== "available" || checkedEmail !== nextEmail) {
            setEmailCheckStatus(checkedEmail ? "stale" : "unavailable");
            setEmailCheckMessage(checkedEmail ? "이메일이 변경되었습니다. 중복체크를 다시 진행해주세요." : "관리자 이메일 중복체크를 진행해주세요.");
            return;
        }
        if (!password) return setPasswordError(ErrMsg["auth/password-empty"]);
        if (!confirm) return setConfirmError(ErrMsg["auth/password-empty"]);

        setLoading(true);
        try {
            await httpsCallable(functions, "adminCreate")({
                name,
                type,
                email: nextEmail,
                password,
                confirm,
                partnerId: sessionStorage.getItem("partnerId") ?? null,
            });
            hide();
        } catch (err: any) {
            console.error(err.message);
            if (ErrMsg[err.message]) {
                if (err.message.includes("name")) setNameError(ErrMsg[err.message]);
                else if (err.message.includes("type")) setTypeError(ErrMsg[err.message]);
                else if (err.message.includes("email")) setEmailError(ErrMsg[err.message]);
                else if (err.message.includes("password-not-match")) setConfirmError(ErrMsg[err.message]);
                else if (err.message.includes("password")) setPasswordError(ErrMsg[err.message]);
            } else {
                alert("관리자 생성 중 에러가 발생했습니다");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal active={active} title="관리자 추가" kicker="계정 생성" copy="현재 파트너사에 연결된 관리자 계정을 생성합니다." close={hide}>
            <div className={common["wrapper--overlay"]}>
                <div className={style["unit--overlay--inputs"]}>
                    <BlockInput type="text" placeholder="이름" value={name} setValue={setName} error={nameError} />
                    <SelectorInput placeholder="구분" value={type} setValue={setType} error={typeError}>
                        <option value="ADMIN">일반 관리자</option>
                        <option value="GROUP_ADMIN">최고 관리자</option>
                    </SelectorInput>
                    <div className={style["email-check-row"]}>
                        <BlockInput type="text" placeholder="이메일" value={email} setValue={setEmail} error={emailError} />
                        <button
                            type="button"
                            className={[style["button--secondary"], style["email-check-button"]].join(" ")}
                            onClick={checkEmail}
                            disabled={emailCheckStatus === "checking" || !email || Boolean(emailError)}
                        >
                            {emailCheckStatus === "checking" ? "확인 중" : "중복체크"}
                        </button>
                    </div>
                    {emailCheckMessage && (
                        <p
                            className={[
                                style["field-message"],
                                emailCheckStatus === "available" && style["field-message--success"],
                                emailCheckStatus !== "available" && style["field-message--error"],
                            ].join(" ")}
                        >
                            {emailCheckMessage}
                        </p>
                    )}
                    <BlockInput type="password" placeholder="비밀번호" value={password} setValue={setPassword} error={passwordError} />
                    <BlockInput type="password" placeholder="비밀번호 확인" value={confirm} setValue={setConfirm} error={confirmError} />
                </div>
            </div>
            <ModalFooter close={hide} primaryText={loading ? "추가 중" : "추가"} onPrimary={create} />
        </Modal>
    );
};

const EditAdmin = ({ admin, close }: { admin: AdminType; close: () => void }) => {
    const init = useRef(true);
    const [name, setName] = useState<string>(admin.name);
    const [nameError, setNameError] = useState<string>("");
    const [type, setType] = useState<string>(admin.type);
    const [typeError, setTypeError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(false);

    useEffect(() => setActive(true), []);
    useEffect(() => setNameError(name && name.length < 2 ? ErrMsg["auth/name-invalid"] : ""), [name]);
    useEffect(() => {
        if (init.current) {
            init.current = false;
            return;
        }
        setTypeError(!type ? ErrMsg["auth/type-invalid"] : "");
    }, [type]);

    const hide = () => {
        setActive(false);
        setTimeout(close, 240);
    };

    const edit = async () => {
        setLoading(true);
        try {
            await updateDoc(doc(db, "admin", admin.uid), { name, type });
            hide();
        } catch (err) {
            console.error(err);
            alert("관리자 정보 수정 중에 에러가 발생했습니다");
        } finally {
            setLoading(false);
        }
    };

    const deleteAdmin = async () => {
        if (!window.confirm("이 관리자 계정을 삭제하시겠습니까?")) return;
        setLoadingDelete(true);
        try {
            await httpsCallable(functions, "adminDelete")({ uid: admin.uid });
            hide();
        } catch (err: any) {
            console.error(err);
            alert(err.message === "auth/same-user" ? "자신은 삭제할 수 없습니다" : "에러가 발생했습니다");
        } finally {
            setLoadingDelete(false);
        }
    };

    return (
        <Modal active={active} title="관리자 수정" kicker="계정 관리" copy={admin.email} close={hide}>
            <div className={common["wrapper--overlay"]}>
                <div className={style["unit--overlay--inputs"]}>
                    <BlockInput type="text" placeholder="이름" value={name} setValue={setName} error={nameError} />
                    <SelectorInput placeholder="구분" value={type} setValue={setType} error={typeError}>
                        <option value="ADMIN">일반 관리자</option>
                        <option value="GROUP_ADMIN">최고 관리자</option>
                    </SelectorInput>
                    <button className={style["button--danger"]} onClick={deleteAdmin}>
                        {loadingDelete ? "삭제 중" : "계정 삭제"}
                    </button>
                </div>
            </div>
            <ModalFooter close={hide} primaryText={loading ? "저장 중" : "저장"} onPrimary={edit} />
        </Modal>
    );
};

const Modal = ({
    active,
    title,
    kicker,
    copy,
    close,
    children,
}: React.PropsWithChildren<{ active: boolean; title: string; kicker: string; copy: string; close: () => void }>) => (
    <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
        <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
            <div className={style["modal-header"]}>
                <div>
                    <p className={style["section-kicker"]}>{kicker}</p>
                    <h4 className={style["modal-title"]}>{title}</h4>
                    <p className={style["modal-copy"]}>{copy}</p>
                </div>
                <button className={style["icon-button"]} onClick={close} aria-label="닫기">
                    ×
                </button>
            </div>
            {children}
        </div>
    </div>
);

const ModalFooter = ({ close, primaryText, onPrimary }: { close: () => void; primaryText: string; onPrimary: () => void }) => (
    <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
        <button className={style["button--secondary"]} onClick={close}>
            취소
        </button>
        <button className={style["button--primary"]} onClick={onPrimary}>
            {primaryText}
        </button>
    </div>
);

const typeLabel = (type?: string | null) => {
    switch (type) {
        case "GROUP_ADMIN":
            return "최고 관리자";
        case "ADMIN":
            return "일반 관리자";
        default:
            return "관리자";
    }
};

export default AdminV2;
